import React, { useContext, useState, useEffect } from "react";
import { UserContext } from "../contexts/UserContext";
import { useNavigate } from 'react-router-dom';
import { usePushNotifications } from './PushNotificationProvider';

function Account({ onClose, onLogoutSuccess }) {
    const navigate = useNavigate();
    const { user, logout } = useContext(UserContext);
    const [showConfirmLogout, setShowConfirmLogout] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const { status: pushStatus, enable: enablePush, disable: disablePush } = usePushNotifications();
    const userRole = user?.rol || user?.rol_id || user?.rolId || null;

    if (!user) return null;

    const roleName = userRole === 1 ? "USUARIO" : userRole === 2 ? "ARRENDADOR" : userRole === 3 ? "ADMIN" : "DESCONOCIDO";

    const goToConfigAccount = () => { navigate('/my-account'); onClose(); };
    const goToDashboard = () => { navigate('/dashboard'); onClose(); };
    const handleLogoutClick = () => setShowConfirmLogout(true);
    const confirmLogout = () => { 
        logout(); 
        setShowConfirmLogout(false); 
        navigate('/'); 
        if (onLogoutSuccess) onLogoutSuccess();
    };
    const cancelLogout = () => setShowConfirmLogout(false);

    const handleDeleteAccount = async () => {
        try {
            setDeleting(true);
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            const token = userData?.token;
            
            if (!token) {
                alert('Sesión expirada');
                logout();
                navigate('/login');
                return;
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_URL || 'http://localhost:9000'}/users/delete-account`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.ok) {
                logout();
                onClose();
                navigate('/');
            } else if (response.status === 401) {
                logout();
                navigate('/login');
            } else {
                alert('Error al eliminar la cuenta');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al eliminar la cuenta');
        } finally {
            setDeleting(false);
            setShowDeleteModal(false);
        }
    };

    const handleOutsideClick = (event) => {
        if (event.target.classList && event.target.classList.contains("account-overlay")) onClose();
    };

    const initials = `${(user.nombre || '').charAt(0)}${(user.apellido || '').charAt(0)}`.toUpperCase();
    const firstName = (user.nombre || '').split(' ')[0] || '';

    return (
        <>
            <div
                className="account-overlay fixed inset-0 z-50"
                onClick={handleOutsideClick}
            >
                <div className="relative w-full h-full">
                    <div className="absolute top-14 right-6 w-80 bg-surface-container-lowest rounded-xl shadow-2xl overflow-hidden">
                        <div className="p-5">
                            <div className="flex items-start justify-between">
                                <div className="text-body-md text-on-surface-variant truncate max-w-[60%]">{user.email}</div>
                                <button
                                    onClick={onClose}
                                    className="text-outline hover:text-on-surface-variant rounded-full p-1 transition-colors"
                                    aria-label="Cerrar"
                                >
                                    <span className="material-symbols-outlined text-lg">close</span>
                                </button>
                            </div>

                            <div className="flex flex-col items-center mt-4">
                                <div className="w-20 h-20 rounded-full bg-brand-500 flex items-center justify-center text-white text-headline-md font-bold shadow-lg">
                                    {initials || user.email.charAt(0).toUpperCase()}
                                </div>
                                <h3 className="mt-3 font-display text-2xl text-ink">¡Hola, {firstName}!</h3>
                                <span className="mt-1 text-label-md uppercase tracking-wider text-brand-500">{roleName}</span>
                                <button
                                    onClick={goToConfigAccount}
                                    className="mt-3 px-4 py-1.5 rounded-full bg-brand-50 text-brand-500 text-label-md hover:bg-brand-100 transition-all"
                                >
                                    Administrar tu Cuenta
                                </button>
                            </div>

                            <div className="mt-5 bg-surface-container-low rounded-xl p-4 space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-label-md uppercase tracking-wider text-outline">Nombre</span>
                                    <span className="text-body-md font-semibold text-on-surface">{user.nombre}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-label-md uppercase tracking-wider text-outline">Apellido</span>
                                    <span className="text-body-md font-semibold text-on-surface">{user.apellido}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-label-md uppercase tracking-wider text-outline">Correo</span>
                                    <span className="text-body-md font-semibold text-on-surface truncate max-w-[50%]">{user.email}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-label-md uppercase tracking-wider text-outline">Teléfono</span>
                                    <span className="text-body-md font-semibold text-on-surface">{user.telefono || '-'}</span>
                                </div>
                            </div>

                            <div className="mt-4 bg-surface-container-low rounded-xl p-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-sm text-outline">notifications</span>
                                        <span className="text-label-md text-on-surface">Notificaciones push</span>
                                    </div>
                                    {pushStatus === 'active' || pushStatus === 'registered' ? (
                                        <button
                                            onClick={disablePush}
                                            className="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium hover:bg-red-100 hover:text-red-700 transition-all"
                                            title="Desactivar notificaciones push"
                                        >
                                            Activadas
                                        </button>
                                    ) : pushStatus === 'requesting-permission' || pushStatus === 'registering-sw' || pushStatus === 'subscribing' || pushStatus === 'getting-key' ? (
                                        <span className="text-xs text-outline animate-pulse">Activando...</span>
                                    ) : pushStatus === 'disabled' || pushStatus === 'inactive' || pushStatus === 'idle' ? (
                                        <button
                                            onClick={enablePush}
                                            className="text-xs bg-surface-container-highest text-outline px-2.5 py-1 rounded-full font-medium hover:bg-brand-50 hover:text-brand-500 transition-all"
                                        >
                                            Activar
                                        </button>
                                    ) : (
                                        <span className="text-xs text-outline">{pushStatus}</span>
                                    )}
                                </div>
                                {pushStatus === 'denied' && (
                                    <div className="mt-1">
                                        <p className="text-xs text-error">Bloqueado por el navegador.</p>
                                        <p className="text-xs text-on-surface-variant mt-0.5">
                                            Haz click en el candado 🔒 junto a la URL → Notificaciones → Permitir
                                        </p>
                                        <button
                                            onClick={enablePush}
                                            className="text-xs text-brand-500 font-medium mt-1 hover:underline"
                                        >
                                            Reintentar después de cambiar
                                        </button>
                                    </div>
                                )}
                                {pushStatus === 'failed' && (
                                    <p className="text-xs text-warning mt-1">Error al activar. Intenta de nuevo.</p>
                                )}
                                {pushStatus === 'active' && (
                                    <button
                                        onClick={async () => {
                                            try {
                                                const token = JSON.parse(localStorage.getItem('user') || '{}').token;
                                                if (!token) return;
                                                await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:9000'}/push/test`, {
                                                    method: 'POST',
                                                    headers: { 'Authorization': `Bearer ${token}` }
                                                });
                                            } catch (e) {
                                                console.error('Test push error:', e);
                                            }
                                        }}
                                        className="text-xs text-brand-500 font-medium mt-1 hover:underline"
                                    >
                                        Enviar notificación de prueba
                                    </button>
                                )}
                            </div>

                            <div className="mt-4 space-y-2">
                                <button
                                    onClick={handleLogoutClick}
                                    className="w-full bg-brand-500 text-white font-semibold py-2.5 rounded-lg hover:bg-brand-500/90 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                                >
                                    <span className="material-symbols-outlined text-sm">logout</span>
                                    Cerrar sesión
                                </button>
                                <button
                                    onClick={() => setShowDeleteModal(true)}
                                    className="w-full text-error text-label-md font-medium py-2 rounded-lg hover:bg-error/10 transition-all flex items-center justify-center gap-2"
                                >
                                    <span className="material-symbols-outlined text-sm">delete</span>
                                    Eliminar mi cuenta
                                </button>
                            </div>

                            {user.rol === 2 && (
                                <button
                                    className="mt-4 w-full text-label-md text-brand-500 font-medium py-2 rounded-lg bg-brand-50 hover:bg-brand-100 transition-all flex items-center justify-center gap-2"
                                    onClick={goToDashboard}
                                >
                                    <span className="material-symbols-outlined text-sm">dashboard</span>
                                    Panel de gestión
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showConfirmLogout && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
                    <div className="bg-surface-container-lowest rounded-xl shadow-2xl w-80 max-w-md p-6">
                        <div className="flex items-center justify-center mb-4">
                            <div className="w-14 h-14 rounded-full bg-brand-50 flex items-center justify-center">
                                <span className="material-symbols-outlined text-2xl text-brand-500">logout</span>
                            </div>
                        </div>
                        <p className="text-center font-display text-2xl text-ink mb-2">¿Cerrar sesión?</p>
                        <p className="text-center text-body-md text-on-surface-variant mb-6">¿Estás seguro de que deseas cerrar sesión?</p>
                        <div className="flex gap-3">
                            <button
                                onClick={cancelLogout}
                                className="flex-1 py-2.5 bg-surface-container-high text-on-surface font-semibold rounded-lg hover:bg-surface-container-highest transition-all"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmLogout}
                                className="flex-1 py-2.5 bg-brand-500 text-white font-semibold rounded-lg hover:bg-brand-500/90 active:scale-[0.98] transition-all"
                            >
                                Sí, salir
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
                    <div className="bg-surface-container-lowest rounded-xl shadow-2xl w-80 max-w-md p-6">
                        <div className="flex items-center justify-center mb-4">
                            <div className="w-14 h-14 rounded-full bg-error-container/30 flex items-center justify-center">
                                <span className="material-symbols-outlined text-2xl text-error">warning</span>
                            </div>
                        </div>
                        <h2 className="font-headline text-headline-md text-on-surface text-center mb-2">
                            ¿Eliminar tu cuenta?
                        </h2>
                        <p className="text-body-md text-on-surface-variant text-center mb-6">
                            Esta acción es irreversible. Se eliminarán todos tus datos y publicaciones.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="flex-1 py-2.5 bg-surface-container-high text-on-surface font-semibold rounded-lg hover:bg-surface-container-highest transition-all"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDeleteAccount}
                                disabled={deleting}
                                className="flex-1 py-2.5 bg-error text-on-error font-semibold rounded-lg hover:bg-error/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {deleting ? (
                                    <>
                                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></span>
                                        Eliminando...
                                    </>
                                ) : (
                                    'Sí, eliminar'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Account;
